import { ReactElement, useEffect, useState } from 'react';
import { Field, FormikProvider, useFormik } from 'formik';
import { Dispatch } from 'redux';
import { WarningRounded, InfoRounded } from '@material-ui/icons';
import { Box, Grid, Typography } from '@material-ui/core';
import { useAuth0 } from '@auth0/auth0-react';
import { useSelector, useDispatch } from 'react-redux';
import { difference, isNumber, omit, orderBy, uniq } from 'lodash';
import { AnySchema } from 'yup';

// Components
import InputBlock from 'components/global/inputBlock/inputBlock';
import Button from 'components/Button/Button';
import { Stepper } from 'components/Stepper';
import { IStepProps } from 'components/Stepper/Step';
import ButtonRow from './buttonRow';
import { LocationDetailsRepeater } from 'components/LocationDetailsRepeater';
import { CompletedActivityCard } from 'layouts/pages/activity/add-activity/components';

// Services
import { FormService } from 'services/FormService';

// Store + Core
import { getActivityFormTemplate } from 'store/formTemplate/actions';
import {
  amaCreditTermIdSelector,
  ipceCreditTermIdSelector,
  outcomeMeasureChildTermsSelector,
  pharmacyCreditTermIdSelector,
  taxonomyRootTermsSelector,
} from 'store/taxonomy/selectors';
import { getTaxonomyTermById } from 'store/taxonomy/actions';
import { getAllBoards, getBoardById } from 'store/board/actions';
import { openRail } from 'store/rail/actions';
import {
  activitySelector,
  activityTypesSelector,
  currentActivitiesSelector,
  liveCourseSelector,
  rssSelector,
} from 'store/activity/selectors';
import { activityTemplateSelector } from 'store/formTemplate/selectors';
import { auth0UserSelector, isJASelector, organizationKindSelector } from 'store/user/selectors';
import { ActivityType, AddActivityFormValues, IBoardSlim, IDictionary, IFormSection, ITaxonomyTerm } from 'core/models';
import { boardSelector, configuredBoardsDictionarySelector, configuredBoardsSelector } from 'store/board/selectors';
import { clearCurrentActivities } from 'store/activity/actions';

// Constants
import { defaultAddFormProps, termSetIds } from 'layouts/pages/activity/constants';
import { BoardTypes, IBCTBoard } from 'layouts/pages/bct/types';

// Misc
import { blankSchema } from './validation/validationSchemas';
import { useFilterHook } from './hooks';
import { getAllowedBoardSelections } from '../utils';

// Styles
import styles from './index.module.scss';

interface IActivityProps {
  activeStep: IStepProps;
  decrementStep(): void;
  formSteps: IStepProps[];
  goToStep(label: string): void;
  initialValues?: AddActivityFormValues;
  isDraft: boolean;
  isFirstStep: boolean;
  isLastStep: boolean;
  isSubmitStep: boolean;
  onSubmit(values: AddActivityFormValues): Promise<void>;
  updateCollaborationSteps(isMoc, isRems): void;
  validationSchemas?: AnySchema[];
}

const AddActivityForm = (props: IActivityProps): ReactElement => {
  const {
    activeStep,
    formSteps,
    decrementStep,
    goToStep: skipToStep,
    isFirstStep,
    isLastStep,
    isSubmitStep,
    onSubmit,
    updateCollaborationSteps,
    validationSchemas,
  } = props;

  const formTitle = 'Add an Activity';
  const formError = 'You have incomplete information on this page. Please complete to move on to the next step.';
  const formAlert = 'Updates can only be made to editable fields.';
  const pharmacyQuestionFields = [
    'targetAudience',
    'pharmacyTypeId',
    'isPharmacyCertificateProgram',
    'pharmacyTopicId',
    'pharmacyLiveDates',
  ];
  const cmePassportQuestionFields = ['detailsUrl', 'isRestrictedAudience', 'participationFeeTypeId'];
  const { isAuthenticated } = useAuth0();

  const [generatedFields, setGeneratedFields] = useState([] as Array<JSX.Element>);
  const [showGlobalError, setShowGlobalError] = useState<boolean>(false);
  const [isLoadingBoards, setIsLoadingBoards] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [requestedBoardIds, setRequestedBoardIds] = useState<string[]>([]);

  const dispatch: Dispatch<any> = useDispatch();

  // Setup Formik.
  const formik = useFormik({
    initialValues: {
      ...props.initialValues,
      ...defaultAddFormProps,
    },
    onSubmit: async (values, { setSubmitting }): Promise<void> => {
      await setSubmitting(true);
      await onSubmit(values);
      await setSubmitting(false);
      await window.scroll({
        behavior: 'smooth',
        left: 0,
        top: 0,
      });
    },
    validateOnBlur: true,
    validateOnChange: false,
    validationSchema: (): AnySchema => {
      if (activeStep && validationSchemas) {
        switch (activeStep?.label) {
          case 'Basics':
            return validationSchemas[0];
          case 'Information for Learners':
            return validationSchemas[1];
          case 'Accreditation Details':
            return validationSchemas[2];
          case 'MOC':
            return validationSchemas[3];
          case 'REMS':
            return validationSchemas[4];
          default:
            return blankSchema;
        }
      }
    },
  });

  const {
    dirty,
    errors,
    handleSubmit,
    initialValues,
    isSubmitting,
    isValid,
    resetForm,
    setErrors,
    setFieldValue,
    setValues,
    touched,
    values,
  } = formik;

  const goToStep = (label: string): void => {
    skipToStep(label);
    setErrors({});
  };

  // Redux State.
  const activityTemplate = useSelector(activityTemplateSelector);
  const activityTypes = useSelector(activityTypesSelector);
  const amaCreditId = useSelector(amaCreditTermIdSelector)?.id;
  const ipceCreditId = useSelector(ipceCreditTermIdSelector)?.id;
  const auth0User = useSelector(auth0UserSelector);
  const baseTerms = useSelector(taxonomyRootTermsSelector);
  const currentActivities = useSelector(currentActivitiesSelector);
  const currentOrganization = useSelector(organizationKindSelector);
  const liveCourseId = useSelector(liveCourseSelector)?.id;
  const pharmacyCreditsTermId = useSelector(pharmacyCreditTermIdSelector)?.id;
  const rssId = useSelector(rssSelector)?.id;
  const boards = useSelector(boardSelector);
  const terms = useSelector(taxonomyRootTermsSelector);
  const nextSequenceNumber: number = useSelector(activitySelector)?.nextSequenceNumber;
  const configuredBoards: IBCTBoard[] = useSelector(configuredBoardsSelector);
  const configurationDictionary: IDictionary<IBCTBoard> = useSelector(configuredBoardsDictionarySelector);
  // Get the L2 terms for outcome measured and make a map of their ids for the useEffect of nested checkboxes.
  const outcomeMeasuredTerms = useSelector(outcomeMeasureChildTermsSelector).map(({ id }: ITaxonomyTerm) => id);
  const isJa: boolean = useSelector(isJASelector);

  // Separate boards by board type
  const configuredMocBoards: IBCTBoard[] = useFilterHook(BoardTypes.CERTIFYING_BOARD, configuredBoards);
  const configuredRemsBoards: IBCTBoard[] = useFilterHook(BoardTypes.REMS, configuredBoards);

  // 4386 - Depending on activity type, some boards need to be filtered
  const activityTypeId: string = values?.typeId;
  const allowedBoardSelections: IBoardSlim[] = getAllowedBoardSelections(boards, activityTypeId);
  const allMocBoards: IBoardSlim[] = allowedBoardSelections?.filter(
    ({ type }: IBoardSlim): boolean => type === BoardTypes.CERTIFYING_BOARD,
  );
  const allRemsBoards: IBoardSlim[] = allowedBoardSelections?.filter(
    ({ type }: IBoardSlim): boolean => type === BoardTypes.REMS,
  );

  // Redux Actions.
  const dispatchGetActivityTemplate = () => dispatch(getActivityFormTemplate());

  const isPharmacyCreditsChecked: boolean = values?.credits?.[pharmacyCreditsTermId] > 0;
  const isPharmacyAbbreviationL: boolean =
    activityTypes.find((type: ActivityType): boolean => type.id === values.typeId)?.pharmacyAbbreviation === 'L';
  const doesAMACreditHaveValue = !!values?.credits?.[amaCreditId];
  const doesIPCECreditHaveValue = !!values?.credits?.[ipceCreditId];

  // This whole `setActiveFieldClass` is not optimal and needs to be refactored. This is happening on every
  // formik state change.
  const setActiveFieldClass = () => {
    // TODO we should be using refs here instead of querying the DOM
    const allSections = document.querySelectorAll('.form-section');
    const allGroups = document.querySelectorAll('.form-group');
    const activeNodes = document.querySelectorAll(`[data-template-id="${activeStep?.label}"]`);
    allSections.forEach((node) => node.classList.remove('active'));
    allGroups.forEach((node) => node.classList.remove('active'));
    activeNodes.forEach((node) => node.classList.add('active'));

    // The following cannot be done via the template because it comes from taxonomy.
    // Hide/Show these group when the page renders.
    if (doesAMACreditHaveValue || (isJa && doesIPCECreditHaveValue)) {
      document.querySelector('[data-group-id="includeInCmeFinderGroup"]')?.classList.remove('hidden');
      document.querySelector('[data-group-id="isRestrictedAudienceGroup"]')?.classList.remove('hidden');
      document.querySelector('[data-group-id="detailsUrlGroup"]')?.classList.remove('hidden');
    } else {
      document.querySelector('[data-group-id="includeInCmeFinderGroup"]')?.classList.add('hidden');
      document.querySelector('[data-group-id="isRestrictedAudienceGroup"]')?.classList.add('hidden');
      document.querySelector('[data-group-id="detailsUrlGroup"]')?.classList.add('hidden');
    }

    if (isPharmacyCreditsChecked) {
      document.querySelector('[data-group-id="pharmacyQuestions"]')?.classList.remove('hidden');
      if (isPharmacyAbbreviationL) {
        document.querySelector('[data-question="pharmacyLiveDates"]')?.parentElement?.classList.remove('hidden');
      } else {
        document.querySelector('[data-question="pharmacyLiveDates"]')?.parentElement?.classList.add('hidden');
      }
    } else {
      document.querySelector('[data-group-id="pharmacyQuestions"]')?.classList.add('hidden');
    }
  };

  useEffect(() => {
    if (currentActivities.length > 1) {
      setIsDisabled(true);
    }
  }, [currentActivities]);

  useEffect(() => {
    if (auth0User && currentOrganization) {
      const getTemplateAsync = async () => {
        await dispatchGetActivityTemplate();
      };
      getTemplateAsync();
    }
  }, [auth0User, currentOrganization]);

  // If more than 1 current activity after first submission, disable all inputs on the first step of Add Activity Form.
  useEffect(() => {
    if (values.recurring === 'recurring-true' && currentActivities.length > 1) {
      const inputNodes = document.querySelectorAll('[data-template-id="Basics"] input');
      const formInputNodes = document.querySelectorAll('[data-template-id="Basics"] .form-input');
      inputNodes.forEach((node: Element): void => node.setAttribute('disabled', 'true'));
      formInputNodes.forEach((node: Element): void => node.classList.add('disabled'));
    } else {
      const inputNodes = document.querySelectorAll('[data-template-id="Basics"] input');
      const formInputNodes = document.querySelectorAll('[data-template-id="Basics"] .form-input');
      inputNodes.forEach((node: Element): void => node.removeAttribute('disabled'));
      formInputNodes.forEach((node: Element): void => node.classList.remove('disabled'));
    }
  }, [values.recurring, currentActivities]);

  // Show the pharmacy questions when the pharmacy checkbox is checked.
  // Cannot be done via the template because it comes from taxonomy.
  useEffect(() => {
    if (isPharmacyCreditsChecked) {
      document.querySelector('[data-group-id="pharmacyQuestions"]')?.classList.remove('hidden');
      // If is-recurring was selected, don't show a number input, show a message instead.
      if (values.recurring === 'recurring-true') {
        const node = document.querySelector('[data-question="pharmacySequenceNumber"]');
        if (node) {
          const message = document.createElement('div');
          message.setAttribute('class', 'caption-text form-input');
          message.innerHTML =
            'For recurring activities, the sequence number for the UAN will be autogenerated for each activity instance.';
          node.parentNode.replaceChild(message, node);
        }
      }
      // set Default show one date field on Pharmacy live date
      const isPharmacyLiveDate = values.pharmacyLiveDates;
      if (!isPharmacyLiveDate || isPharmacyLiveDate?.length === 0) {
        setFieldValue('pharmacyLiveDates', ['']);
      }
    } else {
      document.querySelector('[data-group-id="pharmacyQuestions"]')?.classList.add('hidden');
    }
  }, [isPharmacyCreditsChecked, values.credits, values.recurring]);

  // Since the Pharmacy Questions' visibility is custom, we have to rely on this hook to reset fields on hide
  useEffect(() => {
    if (!isPharmacyCreditsChecked) {
      pharmacyQuestionFields.forEach((fieldKey) => setFieldValue(fieldKey, initialValues[fieldKey]));
    }
  }, [isPharmacyCreditsChecked]);

  // 4793: Since the CME Passport Questions' visibility is custom, we have to rely on this hook to reset fields on hide
  useEffect(() => {
    if (doesAMACreditHaveValue || (doesIPCECreditHaveValue && isJa)) {
      setFieldValue('includeInCmeFinder', 'includeInCmeFinder-true');
    }

    if (!doesAMACreditHaveValue || (!doesIPCECreditHaveValue && isJa)) {
      // Has neither - needs to default includeInCmeFinder to false
      if (!doesAMACreditHaveValue && !doesIPCECreditHaveValue) {
        cmePassportQuestionFields.forEach((fieldKey) => setFieldValue(fieldKey, initialValues?.[fieldKey]));
        setFieldValue('includeInCmeFinder', 'includeInCmeFinder-false');
      }
    }
  }, [doesAMACreditHaveValue, isJa, doesIPCECreditHaveValue]);

  // Default to next available sequence number if one was provided via API
  // Only add to the model if the pharmacy credits are checked
  // Remove from the model when pharmacy credits is unchecked
  useEffect(() => {
    const isSequenceNumberValid = isNumber(values?.pharmacySequenceNumber);
    const isPharmacyCreditsChecked = !!values?.credits?.[`credits${pharmacyCreditsTermId}`];
    if (!isSequenceNumberValid && !!isPharmacyCreditsChecked) {
      setFieldValue('pharmacySequenceNumber', nextSequenceNumber);
    }
    if (isSequenceNumberValid && !isPharmacyCreditsChecked) {
      setFieldValue('pharmacySequenceNumber', undefined);
    }
  }, [nextSequenceNumber, values?.credits]);

  useEffect(() => {
    if (activityTemplate)
      setGeneratedFields(
        FormService.buildForm({
          activeStepLabel: activeStep?.label,
          activities: currentActivities,
          allMocBoards,
          allRemsBoards,
          amaCreditValue: values?.credits?.[amaCreditId] as number,
          configurationDictionary,
          configuredMocBoards,
          configuredRemsBoards,
          formMeta: activityTemplate,
          formikBag: {
            errors,
            handleChange: onSubmit,
            initialValues,
            setFieldValue,
            touched,
            values,
          },
          isJa,
          selectedRemsBoardIds: values?.supportedRemsIds,
          selectedSpecialtyBoardIds: values?.boardMocDetailsSelection,
          terms,
        }),
      );

    if (values.isMoc || values.isRems) updateCollaborationSteps(values.isMoc, values.isRems);
  }, [
    // NOTE add values here that need to conditionally render visible, required, or editable so that the form will rerender when values change
    activeStep?.label,
    activityTemplate,
    terms,
    values.commercialSupportSourceKind,
    values.credits,
    values.hasCommercialSupport,
    values.includeInCmeFinder,
    values.isJointlyProvided,
    values.isMips,
    values.isMoc,
    values.isRems,
    values.markedForCommendationCriteria,
    values.pharmacyLiveDates,
    values.recurring,
    values.typeId,
    ...outcomeMeasuredTerms.map((value: string): string => values[value]),
  ]);

  // On selecting a board, load it's config and disable submission
  useEffect(() => {
    const boardSelections: string[] = values?.boardMocDetailsSelection || [];
    const remsSelections: string[] = values?.supportedRemsIds || [];
    const missingBoardConfigs: string[] = [...boardSelections, ...remsSelections]?.filter(
      (boardId: string): boolean => !configurationDictionary?.[boardId],
    );
    const boardsNotInTransit: string[] = difference(missingBoardConfigs, requestedBoardIds);
    if (boardsNotInTransit?.length) {
      setIsLoadingBoards(true);
      setRequestedBoardIds(uniq([...requestedBoardIds, ...boardsNotInTransit]));
      Promise.all(
        boardsNotInTransit.map(async (boardId: string) => await dispatch(getBoardById(boardId))),
      ).finally(() => setIsLoadingBoards(false));
    }
  }, [configurationDictionary, dispatch, values?.boardMocDetailsSelection, values?.supportedRemsIds]);

  useEffect(() => {
    setShowGlobalError(false);
  }, [activeStep?.label]);

  // Clear out the fields from the model (in omit function below) when typeId changes and is not `RSS` or `Live course`.
  useEffect(() => {
    const isLiveCourseOrRSS = values.typeId === liveCourseId || values.typeId === rssId;
    if (!isLiveCourseOrRSS) {
      setValues(omit(values, ['locationType', 'recurring']));
    }
  }, [liveCourseId, rssId, setValues, values.typeId]);

  useEffect(() => {
    // When updating, if recurring was dropped from the model, keep only the first date/location entry
    if ((!values.recurring || values.recurring?.includes('false')) && values?.tempDatesAndLocation?.length > 1) {
      const singleIndexDatesAndLocation = values.tempDatesAndLocation[0];
      setFieldValue('tempDatesAndLocation', [singleIndexDatesAndLocation]);
    }
  }, [values.recurring, values.tempDatesAndLocation, setFieldValue]);

  // Taxonomy Initialization Hook
  useEffect(() => {
    termSetIds.forEach((id) => {
      if (!baseTerms[id] && isAuthenticated) {
        dispatch(getTaxonomyTermById(id));
      }
    });
  }, []);

  // 4793 - use this hook to set include cme value to false at first step.
  useEffect(() => {
    if (isFirstStep) {
      setFieldValue('includeInCmeFinder', 'includeInCmeFinder-false');
    }
  }, []);

  // Get all boards
  useEffect(() => {
    if (!boards) {
      dispatch(getAllBoards());
    }
  }, [boards, dispatch, isAuthenticated]);

  const handleResetForm = async () => {
    // Reset the currentActivities to a clean state.
    dispatch(clearCurrentActivities());

    // Reset the form values.
    resetForm({
      values: defaultAddFormProps,
    });
    goToStep('Basics');
  };

  setActiveFieldClass();

  const shouldShowLocationInformation = !!values?.locationType?.includes('InPerson');
  const shouldShowRepeater: boolean = values?.recurring === 'recurring-true';
  const buttonText = (): string => {
    const isFirstStep: boolean = activeStep?.label === 'Basics';

    if (isFirstStep && currentActivities.length > 1) return 'Continue';
    if (isSubmitStep) return 'Save Activity';

    return 'Save Draft and Continue';
  };

  return (
    <FormikProvider value={formik}>
      <Stepper handleClick={goToStep} steps={formSteps} />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(e);
        }}
      >
        <div className="form-title-container">
          <div className="title">
            <div className="eyebrow">{formTitle}</div>
            <h4>
              {activityTemplate
                .find((step: IFormSection): boolean => step.Id === activeStep?.label)
                ?.Subtitle?.replace("'Activity Name'", values.title)}
              <Button className="btn-faq" onClick={() => dispatch(openRail())}>
                FAQ
              </Button>
            </h4>
            {activeStep?.label === 'REMS' && (
              <p>
                As a service to the FDA's Risk Evaluation and Mitigation Strategies Program, accredited providers can
                register activities in support of REMS.
              </p>
            )}
            {activeStep?.label === 'MOC' ? (
              <p className={styles['board-subheading']}>
                To add more certifying boards
                <button
                  title="Click to return to Information for Learners"
                  className={styles['cert-board-button']}
                  onClick={() => goToStep('Information for Learners')}
                >
                  click here
                </button>
                .
              </p>
            ) : null}
          </div>
        </div>
        {!isValid && showGlobalError && (
          <div className="error">
            <div className="message">
              <WarningRounded /> {formError}
            </div>
          </div>
        )}
        {/* TODO Need Form Alert Props */}
        {!isValid && false && (
          <div className="info">
            <div className="message">
              <InfoRounded /> {formAlert}
            </div>
          </div>
        )}
        <div data-template-id="Basics" className="form-group">
          <InputBlock
            errors={errors}
            name="title"
            required
            suppressLabel
            title="What's the name of the activity"
            touched={touched}
          >
            <Field
              aria-label="Activity Name"
              disabled={isDisabled}
              id="title"
              name="title"
              placeholder="Enter Activity Name"
              type="text"
            />
          </InputBlock>
        </div>
        <div data-template-id="Basics" className="form-group">
          <InputBlock
            ariaLabel="Activity Format"
            required
            suppressLabel
            errors={errors}
            touched={touched}
            name="typeId"
            role="radiogroup"
            title="What is the activity format?"
          >
            <Box mt={2}>
              {orderBy(activityTypes, 'displayOrder').map(
                (type: ActivityType): ReactElement => {
                  const { description, id, title } = type;
                  return (
                    <Box key={id} mt={2}>
                      <Grid container>
                        <Grid item>
                          <label className="form-input-radio" htmlFor={id}>
                            <Field
                              disabled={isDisabled}
                              id={id}
                              name="typeId"
                              type="radio"
                              value={id}
                              aria-invalid={!!errors?.typeId?.length}
                            />
                            <span className="checkmark" />
                            <Typography variant="srOnly">{title}</Typography>
                          </label>
                        </Grid>
                        <Grid item xs={11}>
                          <div className="label-text">{title}</div>
                          <div className="caption-text">{description}</div>
                        </Grid>
                      </Grid>
                    </Box>
                  );
                },
              )}
            </Box>
          </InputBlock>
        </div>
        {generatedFields}
        <div data-template-id="Basics" className="form-group">
          <LocationDetailsRepeater
            isDisabled={isDisabled}
            formikKey="tempDatesAndLocation"
            shouldShowLocationInformation={shouldShowLocationInformation}
            shouldShowRepeater={shouldShowRepeater}
          />
        </div>
        {activeStep?.label === 'Complete' && (
          <div data-template-id="Complete" className="form-group">
            <h3>{currentActivities.length > 1 ? 'Activities' : 'Activity'} successfully added</h3>
            <CompletedActivityCard
              currentActivities={currentActivities}
              configurationDictionary={configurationDictionary}
              handleAddAnotherActivity={handleResetForm}
              values={values}
            />
          </div>
        )}
        {!isLastStep && (
          <ButtonRow
            ctaLabel={buttonText()}
            isSubmitting={isSubmitting || isLoadingBoards}
            isValid={isValid && dirty}
            onBackClick={(): void => {
              decrementStep();
              setErrors({});
            }}
            onClick={handleSubmit}
            showBack={!isFirstStep}
          />
        )}
        {isLastStep && (
          <Typography variant="srOnly">
            <button tabIndex={-1}>SR only</button>
          </Typography>
        )}
      </form>
    </FormikProvider>
  );
};

export default AddActivityForm;
